import { supabase } from '../lib/supabase'

export type LogAction = 'create' | 'update' | 'delete' | 'view' | 'export' | 'import' | 'login' | 'logout' | 'other'
export type EntityType =
  | 'user'
  | 'user_group'
  | 'organization'
  | 'department'
  | 'user_permissions'
  | 'permission'
  | 'module'
  | 'parameter'
  | 'quality_category'
  | 'quality_incident'
  | 'quality_incident_stage'
  | 'quality_incident_type'
  | 'quality_incident_origin'
  | 'quality_improcedent_reason'
  | 'email_template'
  | 'superapp_dashboard_milestone'
  | 'social_post'
  | 'social_post_comment'
  | 'ia_prompts_agente'
  | 'ia_n8n_flow'
  | 'ia_base_conhecimento'
  | 'ia_documento'
  | 'informativo_boletim'
  | 'process_flowchart'
  | 'marketing_clipping'
  | 'marketing_clipping_vehicle'
  | 'marketing_clipping_category'
  | 'marketing_clipping_veiculo'
  | 'marketing_clipping_categoria'
  | 'marketing_clipping_search_site'
  | 'marketing_clipping_search_config'
  | 'marketing_clipping_ia_config'
  | 'marketing_news_alert'
  | 'marketing_game'
  | 'marketing_game_session'
  | 'support_center_appointment'
  | 'support_center_appointment_attachment'
  | 'support_center_triage'
  | 'support_center_specialty'
  | 'support_center_specialty_user'
  | 'support_center_professional_role'
  | 'support_center_user_unavailability'
  | 'support_center_holiday'
  | 'support_center_assistance'
  | 'support_center_form'
  | 'support_center_cancel_reason'
  | 'support_center_referral_origin'
  | 'support_center_demand_type'
  | 'marketing_stock_item'
  | 'marketing_stock_item_photo'
  | 'marketing_stock_category'
  | 'marketing_stock_supplier'
  | 'supplier'
  | 'payment_method'
  | 'payment_source'
  | 'marketing_stock_location'
  | 'marketing_stock_movement'
  | 'marketing_request'
  | 'marketing_request_stage'
  | 'marketing_request_attachment'
  | 'contract'
  | 'contract_category'
  | 'contract_amount'
  | 'contract_attachment'
  | 'marketing_invoice'
  | 'supplier_quote'
  | 'marketing_payment'
  | 'marketing_campaign'
  | 'marketing_event'
  | 'marketing_material'
  | 'marketing_report'
  | 'marketing_expense'
  | 'other'

export interface LogMetadata {
  [key: string]: any
  old_values?: Record<string, any>
  new_values?: Record<string, any>
  filters?: Record<string, any>
  additional_info?: string
}

export interface UserActivityLog {
  id: string
  user_id: string
  action: LogAction
  entity_type: EntityType
  entity_id?: string | null
  description: string
  metadata?: LogMetadata | null
  page_url?: string | null
  ip_address?: string | null
  user_agent?: string | null
  created_at: string
}

/** Linha de `user_activity_logs` com join opcional em `profiles` (select aninhado). */
export type LogWithUser = UserActivityLog & {
  profiles?: { id: string; name: string | null } | null
}

export interface CreateLogParams {
  action: LogAction
  entityType: EntityType
  entityId?: string | null
  description: string
  metadata?: LogMetadata
  userId?: string
}

/**
 * Serviço para registrar logs de atividade do usuário
 */
class UserLogsService {
  /**
   * Registra uma ação genérica do usuário.
   * @returns true se o log foi persistido com sucesso
   */
  async logUserAction(params: CreateLogParams): Promise<boolean> {
    try {
      let userId: string | undefined

      // Se userId foi fornecido explicitamente, usar ele (útil para login/logout)
      if (params.userId) {
        userId = params.userId
      } else {
        // Caso contrário, tentar obter do usuário autenticado
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) {
          console.warn('Tentativa de registrar log sem usuário autenticado e sem userId fornecido')
          return false
        }
        userId = user.id
      }

      if (!userId) {
        console.warn('Não foi possível determinar o userId para o log')
        return false
      }

      // Capturar informações do navegador
      const pageUrl = typeof window !== 'undefined' ? window.location.href : null
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null

      // Preparar dados do log
      const logData = {
        user_id: userId,
        action: params.action,
        entity_type: params.entityType,
        entity_id: params.entityId || null,
        description: params.description,
        metadata: params.metadata || null,
        page_url: pageUrl,
        user_agent: userAgent,
        // ip_address será capturado no backend se necessário
      }

      // Inserir log (não bloqueia em caso de erro)
      const { error } = await supabase
        .from('user_activity_logs')
        .insert(logData)

      if (error) {
        console.error('Erro ao registrar log:', {
          error,
          action: params.action,
          userId,
          entityType: params.entityType,
          description: params.description
        })
        return false
      }

      return true
    } catch (error) {
      console.error('Erro ao registrar log de atividade:', error)
      return false
    }
  }

  /**
   * Atalho para registrar criação de entidade
   */
  async logCreate(
    entityType: EntityType,
    entityId: string | null,
    description: string,
    metadata?: LogMetadata
  ): Promise<void> {
    await this.logUserAction({
      action: 'create',
      entityType,
      entityId,
      description,
      metadata,
    })
  }

  /**
   * Atalho para registrar atualização de entidade
   */
  async logUpdate(
    entityType: EntityType,
    entityId: string | null,
    description: string,
    oldValues?: Record<string, any>,
    newValues?: Record<string, any>,
    additionalMetadata?: LogMetadata
  ): Promise<void> {
    const metadata: LogMetadata = {
      ...additionalMetadata,
      old_values: oldValues,
      new_values: newValues,
    }

    await this.logUserAction({
      action: 'update',
      entityType,
      entityId,
      description,
      metadata,
    })
  }

  /**
   * Atalho para registrar exclusão de entidade
   */
  async logDelete(
    entityType: EntityType,
    entityId: string | null,
    description: string,
    metadata?: LogMetadata
  ): Promise<void> {
    await this.logUserAction({
      action: 'delete',
      entityType,
      entityId,
      description,
      metadata,
    })
  }

  /**
   * Atalho para registrar visualização de entidade
   */
  async logView(
    entityType: EntityType,
    entityId: string | null | undefined,
    description?: string
  ): Promise<void> {
    const defaultDescription = entityId
      ? `Visualizou ${entityType} ${entityId}`
      : `Visualizou lista de ${entityType}s`

    await this.logUserAction({
      action: 'view',
      entityType,
      entityId: entityId || null,
      description: description || defaultDescription,
    })
  }

  /**
   * Buscar logs com filtros
   */
  async getLogs(filters?: {
    userId?: string
    action?: LogAction
    entityType?: EntityType
    entityId?: string
    dateFrom?: string
    dateTo?: string
    search?: string
    page?: number
    limit?: number
  }): Promise<{ logs: LogWithUser[]; total: number }> {
    try {
      let query = supabase
        .from('user_activity_logs')
        .select(`
          *,
          profiles!user_activity_logs_user_id_fkey(id, name)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })

      // Aplicar filtros
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId)
      }

      if (filters?.action) {
        query = query.eq('action', filters.action)
      }

      if (filters?.entityType) {
        query = query.eq('entity_type', filters.entityType)
      }

      if (filters?.entityId) {
        query = query.eq('entity_id', filters.entityId)
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom)
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo)
      }

      if (filters?.search) {
        query = query.ilike('description', `%${filters.search}%`)
      }

      // Paginação
      const page = filters?.page || 1
      const limit = filters?.limit || 20
      const from = (page - 1) * limit
      const to = from + limit - 1

      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) {
        throw error
      }

      return {
        logs: (data || []) as LogWithUser[],
        total: count || 0,
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error)
      throw error
    }
  }
}

// Exportar instância singleton
export const userLogsService = new UserLogsService()

// Exportar funções de conveniência
export const logUserAction = (params: CreateLogParams) => userLogsService.logUserAction(params)
export const logCreate = (entityType: EntityType, entityId: string | null, description: string, metadata?: LogMetadata) =>
  userLogsService.logCreate(entityType, entityId, description, metadata)
export const logUpdate = (
  entityType: EntityType,
  entityId: string | null,
  description: string,
  oldValues?: Record<string, any>,
  newValues?: Record<string, any>,
  additionalMetadata?: LogMetadata
) => userLogsService.logUpdate(entityType, entityId, description, oldValues, newValues, additionalMetadata)
export const logDelete = (entityType: EntityType, entityId: string | null, description: string, metadata?: LogMetadata) =>
  userLogsService.logDelete(entityType, entityId, description, metadata)
export const logView = (entityType: EntityType, entityId: string | null | undefined, description?: string) =>
  userLogsService.logView(entityType, entityId, description)
